import React, {useRef, useState, useEffect} from 'react';
import {Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography} from '@mui/material';
import FunctionsData from '../../Functions/FunctionsData';

// Simple signature canvas without external deps
function SignatureCanvas({width=500, height=150, onChange}){
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  function start(e){
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const rect = canvasRef.current.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo((e.clientX - rect.left), (e.clientY - rect.top));
  }
  function move(e){
    if(!drawing.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const rect = canvasRef.current.getBoundingClientRect();
    ctx.lineTo((e.clientX - rect.left), (e.clientY - rect.top));
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    if(onChange) onChange(canvasRef.current.toDataURL('image/png'));
  }
  function end(){
    drawing.current = false;
    if(onChange) onChange(canvasRef.current.toDataURL('image/png'));
  }
  function clear(){
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0,0,canvasRef.current.width, canvasRef.current.height);
    if(onChange) onChange(null);
  }

  return (
    <div>
      <canvas ref={canvasRef} width={width} height={height} style={{border:'1px solid #ccc', touchAction:'none'}}
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={(e)=>{e.preventDefault(); start(e.touches[0]);}}
              onTouchMove={(e)=>{e.preventDefault(); move(e.touches[0]);}}
              onTouchEnd={(e)=>{e.preventDefault(); end();}}
      />
      <div style={{marginTop:8}}>
        <Button size="small" onClick={clear}>Clear</Button>
      </div>
    </div>
  )
}

function buildAgreementText(vet){
  const buyerName = 'RNZSPCA';
  const buyerGst = '043-731-793';
  const buyerAddress = `SPCA National Support Office\n6 Rawiri Place\nHobsonville`;

  const sellerName = vet?.business_name || '[Vet\u2019s Legal Name]';
  const sellerAddress = [vet?.formatted_address, vet?.street, vet?.suburb, vet?.city].filter(Boolean).join('\n') || '[Vet\u2019s Address]';

  return `Buyer-Created Taxable Supply Information Agreement\n\nThis Agreement is made on [Date] between:\n\nBuyer:\nName:\t\t\t${buyerName}\nGST Number:\t\t${buyerGst}\nAddress:\t\t${buyerAddress}\nSeller:\nName: ${sellerName}\nAddress: ${sellerAddress}\n\n1. Purpose\nThis agreement sets out the terms under which the Buyer will issue buyer-created taxable supply information for supplies made by the Seller, in accordance with the Goods and Services Tax Act 1985 and Inland Revenue guidelines.\n\n2. Agreement Terms\nBoth parties confirm they are GST-registered in New Zealand.\n\nThis agreement applies only to supplies and transactions classified as de-Sexing activities. It does not apply to any other types of supplies or activities.\n\nThe Buyer will issue taxable supply information for each taxable supply made by the Seller to the Buyer. The Seller agrees not to issue taxable supply information for these supplies.\n\nThe Buyer will ensure that each document includes:\n  - The Buyer\u2019s and Seller\u2019s GST numbers\n  - A description of the goods or services\n  - The date of supply\n  - The amount of consideration\n  - The GST component\n\nThe seller can elect to have 7 day payment terms for de-sexing activities as part of the arrangement.\n\nThe parties are not required to record the reasons for using buyer-created taxable supply information, as this agreement is part of their normal terms of business.\n\n3. Duration\nThis agreement remains in effect until terminated in writing by either party with 30 days\u2019 notice. The seller can also opt out via the SPCA\u2019s De-Sexing Portal. After the notice period, the seller will return to issuing monthly invoices and revert back to payment terms of 20th of the month following supply.\n\n4. Compliance\nBoth parties agree to comply with all relevant GST legislation and Inland Revenue requirements.\n\n5. Signatures\n\nBuyer Signature:\nName: _SPCA NZ__________________\nTitle: __________________________\nDate: __________________________\nSignature: ______________________\n\nSeller Signature:\nName: __________________________\nTitle: __________________________\nDate: __________________________\nSignature: ______________________\n\n`;
}

export default function BCTIContractModal({open, onClose, vet_id, onSaved, paymentTerm='ME+20D'}){
  const [signatures, setSignatures] = useState([ { label: 'Seller (Vet)', data: null } ]);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [vetInfo, setVetInfo] = useState(null);

  useEffect(() => {
    if(!open) return;
    // fetch vet details to prefill seller fields
    FunctionsData.fetchDataGet(
      FunctionsData.getApiUrl('/api/vet/') + vet_id,
      (d) => {
        const arr = Array.isArray(d) ? d : [];
        setVetInfo(arr[0] || null);
      },
      true,
      () => {}
    );
  }, [open, vet_id]);

  function onFileChange(e, idx){
    const f = e.target.files && e.target.files[0];
    if(!f) return;
    const reader = new FileReader();
    reader.onload = function(ev){
      const copy = [...signatures];
      copy[idx] = {...copy[idx], data: ev.target.result};
      setSignatures(copy);
    };
    reader.readAsDataURL(f);
  }

  function updateSignatureData(dataUrl, idx){
    const copy = [...signatures];
    copy[idx] = {...copy[idx], data: dataUrl};
    setSignatures(copy);
  }

  function addSignatureField(){
    setSignatures([...signatures, { label: 'Signer ' + (signatures.length + 1), data: null }]);
  }

  function removeSignatureField(idx){
    const copy = signatures.filter((s, i) => i !== idx);
    setSignatures(copy);
  }

  async function handleSubmit(){
    const anySig = signatures.some(s=> !!s.data);
    if(!anySig){
      alert('Please provide at least one signature (draw or upload).');
      return;
    }
    setLoading(true);
    try{
      // Build payload matching backend route field names
      const agreementText = buildAgreementText(vetInfo);
      const now = new Date().toISOString();
      const signaturesPayload = signatures.map((s, idx) => ({
        // Backend original sample expects name, email, role, signedAt; we also add imageBase64 for embedding
        name: vetInfo?.business_name || s.label || 'Vet',
        email: vetInfo?.email || '',
        role: s.label || `Signer ${idx+1}`,
        signedAt: now,
        imageBase64: s.data // base64 PNG from canvas or upload
      }));
      const payload = {
        agreementText, // server expects camelCase
        signatures: signaturesPayload,
        notes,
        optIn: true,
        paymentTermsConfirmed: paymentTerm === '7D',
        paymentTerm,
        generatePdf: true
      };
      const res = await FunctionsData.postData(FunctionsData.getApiUrl('/api/vet/') + vet_id + '/bcti', payload);
      // Response shape per provided API: { data: { filename, pdf_base64 } }
      const data = res?.data;
      if (data) {
        if (data.pdf_url) {
          const a = document.createElement('a');
          a.href = data.pdf_url.startsWith('http') ? data.pdf_url : FunctionsData.getApiUrl(data.pdf_url);
          a.target = '_blank';
          if (data.filename) a.download = data.filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
        } else if (data.pdf_base64) {
          const b64 = data.pdf_base64;
          try {
            const byteCharacters = atob(b64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const blobUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = data.filename || `bcti_${vet_id}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(blobUrl);
          } catch(pdfErr){
            console.error('PDF base64 decode failed', pdfErr);
          }
        }
      }
      if(onSaved) onSaved();
      onClose();
    }catch(err){
      console.error(err);
      alert('Failed to save agreement.');
    }
    setLoading(false);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>BCTI Agreement & e-sign</DialogTitle>
      <DialogContent>
        <Typography variant="body2" style={{marginBottom:12}}>
          Please review the BCTI Agreement. By signing you confirm acceptance of the 7-day payment terms.
        </Typography>

        <div style={{border: '1px solid #eee', padding: 12, maxHeight: 360, overflow:'auto', marginBottom:12}}>
          <Typography variant="body2" style={{whiteSpace:'pre-line'}}>{ buildAgreementText(vetInfo) }</Typography>
        </div>

        <div style={{marginTop:12}}>
          <Typography variant="subtitle2">Signatures (draw or upload) — you can add multiple signers</Typography>
          {signatures.map((s, idx) => (
            <div key={idx} style={{border:'1px dashed #eee', padding:8, marginTop:8}}>
              <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                <TextField size="small" label="Label" value={s.label} onChange={(e)=>{
                  const copy = [...signatures]; copy[idx] = {...copy[idx], label: e.target.value}; setSignatures(copy);
                }} />
                <div>
                  {signatures.length > 1 && <Button size="small" onClick={() => removeSignatureField(idx)}>Remove</Button>}
                </div>
              </div>
              <div style={{marginTop:8}}>
                <SignatureCanvas onChange={(d)=>updateSignatureData(d, idx)} />
              </div>
              <div style={{marginTop:8}}>
                <input type="file" accept="image/*" onChange={(e)=>onFileChange(e, idx)} />
                {s.data && <div style={{marginTop:8}}><img src={s.data} alt="sig" style={{maxHeight:80}}/></div>}
              </div>
            </div>
          ))}
          <div style={{marginTop:8}}>
            <Button size="small" onClick={addSignatureField}>Add another signer</Button>
          </div>
        </div>

        <div style={{marginTop:12}}>
          <TextField label="Notes (optional)" multiline fullWidth rows={3} value={notes} onChange={(e)=>setNotes(e.target.value)} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>{loading? 'Saving...' : 'Sign & Submit'}</Button>
      </DialogActions>
    </Dialog>
  )
}
